/**
 * The backup format, and the only code path in this app that accepts hostile
 * input (T-502).
 *
 * ADR-0003 puts three mechanisms between the user and total loss, and this is
 * the only one that can actually recover anything: there is no server, so when
 * a browser profile is cleared this file is the difference between losing a
 * fortnight and losing everything. An export path without a verified import
 * path is not a backup, it is a file — which is why reading is here beside
 * writing and is tested far harder.
 *
 * Two properties matter more than anything else here:
 *
 *   - **The round trip is exact.** Export, import into an empty database, and
 *     every entity comes back identical, ids and timestamps included.
 *   - **A file this build cannot understand is refused, never guessed at.** A
 *     partial read of a newer schema silently drops the fields it does not
 *     know and writes the result back, destroying data a newer build recorded.
 *
 * The format is plain, indented JSON on purpose. A user should be able to open
 * it, recognise their data, and — at the limit — recover it by hand.
 */

import { err, invalid, ok, type Result, type ValidationError } from '../model/result.js'
import type { Entry, Measurement, Tag } from '../model/entry.js'
import type { CheckInTemplate } from '../model/template.js'
import { dayKey, makeTimestamp, wallClockOf, type Timestamp } from '../model/timestamp.js'
import {
  validateTrackKind,
  type EnumOption,
  type Track,
  type TrackKind,
  type ValueWord,
} from '../model/track.js'
import type { Clock } from '../ports/clock.js'
import type { ExportFile } from '../ports/exporter.js'
import { CORE_VERSION, SCHEMA_VERSION } from '../version.js'

/**
 * A magic string, so a JSON file that is not a backup is refused as such.
 *
 * Without it the first sign of importing the wrong file is a plan reporting
 * that everything will be deleted and nothing added.
 */
export const BACKUP_FORMAT = 'anytracker-backup'

export interface BackupCounts {
  readonly tracks: number
  readonly entries: number
  readonly tags: number
  readonly templates: number
  readonly settings: number
}

export interface BackupData {
  /** Archived tracks included — they are what history resolves against. */
  readonly tracks: readonly Track[]
  readonly entries: readonly Entry[]
  readonly tags: readonly Tag[]
  readonly templates: readonly CheckInTemplate[]
  /**
   * The settings store verbatim.
   *
   * This is where reminder schedules will live (T-701), which is why the
   * backup carries the whole store rather than a list of keys it knows about:
   * a key added later would otherwise be silently absent from every backup
   * taken before someone remembered to add it here.
   */
  readonly settings: Readonly<Record<string, string>>
}

export interface Backup {
  readonly format: typeof BACKUP_FORMAT
  readonly schemaVersion: number
  readonly appVersion: string
  readonly exportedAt: Timestamp
  /**
   * What the file claims to contain.
   *
   * Checked against what it actually contains on read. A truncated file
   * usually fails to parse; one truncated at exactly the wrong place does not,
   * and this is what catches that.
   */
  readonly counts: BackupCounts
  readonly data: BackupData
}

export function countOf(data: BackupData): BackupCounts {
  return {
    tracks: data.tracks.length,
    entries: data.entries.length,
    tags: data.tags.length,
    templates: data.templates.length,
    settings: Object.keys(data.settings).length,
  }
}

export function makeBackup(data: BackupData, clock: Clock): Backup {
  return {
    format: BACKUP_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    appVersion: CORE_VERSION,
    exportedAt: clock.now(),
    counts: countOf(data),
    data,
  }
}

function pad(value: number, width = 2): string {
  return String(value).padStart(width, '0')
}

/** `anytracker-backup-2026-07-29-1432.json`. */
export function backupFilename(backup: Backup): string {
  const wall = wallClockOf(backup.exportedAt)
  // Down to the minute, because two backups on one day is ordinary — before
  // and after a restore, most obviously — and a downloads folder that
  // disambiguates them with "(1)" tells you nothing about which is which.
  return `anytracker-backup-${dayKey(backup.exportedAt)}-${pad(wall.hour)}${pad(wall.minute)}.json`
}

export function backupToFile(backup: Backup): ExportFile {
  return {
    filename: backupFilename(backup),
    mimeType: 'application/json',
    // Indented, and it stays indented. This file is the last resort, and the
    // last resort has to be readable by a person with a text editor.
    content: JSON.stringify(backup, null, 2),
  }
}

// ─── Reading ──────────────────────────────────────────────────────────────────

/**
 * A schema upgrade, applied to the raw parsed data before it is validated.
 *
 * Migrations run on `unknown`, not on domain types: the whole point is that the
 * data is in a shape this build's types no longer describe. Each one moves the
 * data from `from` to `from + 1`, and they are applied in order.
 */
export interface BackupMigration {
  readonly from: number
  readonly describe: string
  readonly migrate: (data: unknown) => unknown
}

/**
 * Every migration this build knows, in order.
 *
 * Empty, and honestly so: schema version 1 is the first release, so no backup
 * older than the current schema can exist yet. The **runner** is real and
 * tested — see `applyMigrations` and its tests — so the first genuine schema
 * change adds one entry here rather than inventing the mechanism under time
 * pressure, which is exactly when a migration gets written badly.
 */
export const BACKUP_MIGRATIONS: readonly BackupMigration[] = []

/**
 * Walks the data forward one version at a time.
 *
 * Exported so its behaviour can be asserted with fixture migrations rather than
 * only through whatever `BACKUP_MIGRATIONS` happens to contain — a mechanism
 * with no entries is untested by definition, and the first real migration is
 * not the moment to discover the runner is wrong.
 */
export function applyMigrations(
  data: unknown,
  fromVersion: number,
  toVersion: number,
  migrations: readonly BackupMigration[] = BACKUP_MIGRATIONS,
): Result<unknown> {
  let current = data
  for (let version = fromVersion; version < toVersion; version++) {
    const step = migrations.find((migration) => migration.from === version)
    if (step === undefined) {
      return err<unknown>(
        invalid(
          'backup.schemaUnsupported',
          `This backup was saved by a version of the app too old to read (data version ${String(fromVersion)}). ` +
            `Nothing has been changed.`,
        ),
      )
    }
    current = step.migrate(current)
  }
  return ok(current)
}

/**
 * How many problems are reported before the rest are summarised.
 *
 * A file corrupted early produces one error per record, and ten thousand of
 * them is not a message — it is a stack trace with no stack. The count is still
 * reported truthfully.
 */
const MAX_REPORTED_ERRORS = 20

class Problems {
  readonly #errors: ValidationError[] = []
  #suppressed = 0

  add(code: string, message: string, field?: string): void {
    if (this.#errors.length < MAX_REPORTED_ERRORS) {
      this.#errors.push(invalid(code, message, field))
    } else {
      this.#suppressed += 1
    }
  }

  get any(): boolean {
    return this.#errors.length > 0
  }

  collected(): ValidationError[] {
    if (this.#suppressed === 0) {
      return this.#errors
    }
    return [
      ...this.#errors,
      invalid(
        'backup.moreProblems',
        `…and ${String(this.#suppressed)} more problems in this file.`,
      ),
    ]
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(
  record: Record<string, unknown>,
  key: string,
  where: string,
  problems: Problems,
): string | null {
  const value = record[key]
  if (typeof value !== 'string') {
    problems.add('backup.field.type', `${where} is missing a valid "${key}".`, where)
    return null
  }
  return value
}

function readOptionalString(
  record: Record<string, unknown>,
  key: string,
  where: string,
  problems: Problems,
): string | undefined | null {
  const value = record[key]
  if (value === undefined) {
    return undefined
  }
  if (typeof value !== 'string') {
    problems.add('backup.field.type', `${where} has a non-text "${key}".`, where)
    return null
  }
  return value
}

function readNumber(
  record: Record<string, unknown>,
  key: string,
  where: string,
  problems: Problems,
): number | null {
  const value = record[key]
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    problems.add('backup.field.type', `${where} is missing a valid "${key}".`, where)
    return null
  }
  return value
}

function readBoolean(
  record: Record<string, unknown>,
  key: string,
  where: string,
  problems: Problems,
): boolean | null {
  const value = record[key]
  if (typeof value !== 'boolean') {
    problems.add('backup.field.type', `${where} is missing a valid "${key}".`, where)
    return null
  }
  return value
}

function readTimestampField(
  record: Record<string, unknown>,
  key: string,
  where: string,
  problems: Problems,
): Timestamp | null {
  const value = record[key]
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} is missing a valid "${key}".`, where)
    return null
  }
  const epochMs = readNumber(value, 'epochMs', `${where}.${key}`, problems)
  const timeZone = readString(value, 'timeZone', `${where}.${key}`, problems)
  if (epochMs === null || timeZone === null) {
    return null
  }
  const made = makeTimestamp(epochMs, timeZone)
  if (!made.ok) {
    // Strict on purpose. A zone this device cannot resolve makes every
    // wall-clock reading of that entry throw, and silently substituting a
    // different zone would move the entry to another day — the corruption
    // ADR-0006 exists to prevent.
    problems.add(
      'backup.timestamp.invalid',
      `${where}.${key} uses a time zone this device does not recognise ("${timeZone}").`,
      where,
    )
    return null
  }
  return made.value
}

function readStringArray(
  record: Record<string, unknown>,
  key: string,
  where: string,
  problems: Problems,
): string[] | null {
  const value = record[key]
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    problems.add('backup.field.type', `${where} has an invalid "${key}".`, where)
    return null
  }
  return value as string[]
}

function readEnumOptions(
  value: unknown,
  where: string,
  problems: Problems,
): EnumOption[] | null {
  if (!Array.isArray(value)) {
    problems.add('backup.field.type', `${where} has invalid options.`, where)
    return null
  }
  const options: EnumOption[] = []
  for (const raw of value) {
    if (!isRecord(raw)) {
      problems.add('backup.field.type', `${where} has an invalid option.`, where)
      return null
    }
    const id = readString(raw, 'id', `${where} option`, problems)
    const label = readString(raw, 'label', `${where} option`, problems)
    const score = raw['score']
    if (id === null || label === null) {
      return null
    }
    if (score !== null && (typeof score !== 'number' || !Number.isFinite(score))) {
      problems.add(
        'backup.field.type',
        `${where} option "${label}" has an invalid score.`,
        where,
      )
      return null
    }
    const colour = readOptionalString(raw, 'colour', `${where} option`, problems)
    const archived = raw['archived']
    if (archived !== undefined && typeof archived !== 'boolean') {
      problems.add(
        'backup.field.type',
        `${where} option "${label}" has an invalid archived flag.`,
        where,
      )
      return null
    }
    options.push({
      id,
      label,
      score,
      ...(colour === undefined || colour === null ? {} : { colour }),
      ...(archived === undefined ? {} : { archived }),
    })
  }
  return options
}

function readTrackKind(
  value: unknown,
  where: string,
  problems: Problems,
): TrackKind | null {
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} is missing its value type.`, where)
    return null
  }
  switch (value['kind']) {
    case 'numeric': {
      const min = readNumber(value, 'min', where, problems)
      const max = readNumber(value, 'max', where, problems)
      const step = readNumber(value, 'step', where, problems)
      const decimals = readNumber(value, 'decimals', where, problems)
      if (min === null || max === null || step === null || decimals === null) {
        return null
      }
      const words = readValueWords(value['words'], where, problems)
      return {
        kind: 'numeric',
        min,
        max,
        step,
        decimals,
        ...(words === undefined ? {} : { words }),
      }
    }
    case 'boolean': {
      const trueLabel = readString(value, 'trueLabel', where, problems)
      const falseLabel = readString(value, 'falseLabel', where, problems)
      if (trueLabel === null || falseLabel === null) {
        return null
      }
      return { kind: 'boolean', trueLabel, falseLabel }
    }
    case 'enum': {
      const ordered = readBoolean(value, 'ordered', where, problems)
      const options = readEnumOptions(value['options'], where, problems)
      if (ordered === null || options === null) {
        return null
      }
      return { kind: 'enum', ordered, options }
    }
    default:
      problems.add(
        'backup.track.kind.unknown',
        `${where} has a value type this version does not understand.`,
        where,
      )
      return null
  }
}

/**
 * Value words, read permissively (T-807).
 *
 * A word that is out of range, over-long or duplicated is **kept**, not
 * refused. Words are a label on a scale: the worst a bad one can do is read
 * oddly, and the rules about them are exactly the kind that get tightened later
 * — a maximum shortened, a cap lowered. Refusing a year of history because a
 * word no longer passes today's check is the failure this reader exists to
 * avoid. Loosened, never tightened.
 *
 * Anything that is not shaped like a word at all is dropped, because there is
 * nothing to keep.
 */
function readValueWords(
  value: unknown,
  where: string,
  problems: Problems,
): readonly ValueWord[] | undefined {
  if (value === undefined || value === null) {
    return undefined
  }
  if (!Array.isArray(value)) {
    problems.add('backup.track.words.type', `${where} has invalid scale words.`, where)
    return undefined
  }

  const words: ValueWord[] = []
  for (const raw of value) {
    if (!isRecord(raw)) {
      continue
    }
    const at = raw['at']
    const label = raw['label']
    if (typeof at === 'number' && Number.isFinite(at) && typeof label === 'string') {
      words.push({ at, label })
    }
  }
  return words.length === 0 ? undefined : words
}

function readTrack(value: unknown, index: number, problems: Problems): Track | null {
  const where = `Track ${String(index + 1)}`
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} is not an object.`, where)
    return null
  }

  const id = readString(value, 'id', where, problems)
  const name = readString(value, 'name', where, problems)
  const archived = readBoolean(value, 'archived', where, problems)
  const position = readNumber(value, 'position', where, problems)
  const createdAt = readTimestampField(value, 'createdAt', where, problems)
  const updatedAt = readTimestampField(value, 'updatedAt', where, problems)
  const kind = readTrackKind(value['kind'], where, problems)
  const unit = readOptionalString(value, 'unit', where, problems)
  const description = readOptionalString(value, 'description', where, problems)
  // Absent in every backup written before T-810, and absent on most tracks
  // since — `questionFor` derives one, so there is nothing to migrate.
  const question = readOptionalString(value, 'question', where, problems)

  if (
    id === null ||
    name === null ||
    archived === null ||
    position === null ||
    createdAt === null ||
    updatedAt === null ||
    kind === null ||
    unit === null ||
    description === null ||
    question === null
  ) {
    return null
  }

  const track: Track = {
    id,
    name,
    kind,
    archived,
    position,
    createdAt,
    updatedAt,
    ...(unit === undefined ? {} : { unit }),
    ...(description === undefined ? {} : { description }),
    ...(question === undefined ? {} : { question }),
  }

  /*
   * The **value type** goes through the same validator the editor uses.
   *
   * Not the whole of `validateTrack`, deliberately. The kind is what every
   * chart, axis and value check is built on — a numeric track whose minimum
   * exceeds its maximum produces NaN in an SVG path and no explanation. The
   * rest of `validateTrack` is cosmetic (name and description lengths) and is
   * exactly the kind of rule that gets tightened later; refusing a year of
   * history because a limit moved after the backup was written would be a far
   * worse failure than a track with an over-long name.
   *
   * The rule that follows from that, and it is load-bearing: **validation of
   * already-stored data may be loosened, never tightened.**
   */
  const validated = validateTrackKind(stripWords(kind))
  if (!validated.ok) {
    for (const error of validated.errors) {
      problems.add(error.code, `${where} ("${name}"): ${error.message}`, where)
    }
    return null
  }
  return track
}

/**
 * The kind with its words removed, for the validation above.
 *
 * The check is about the **value type** — the thing every chart and axis is
 * built on. Words are decoration on top of it, and running them through the
 * same gate would make a shortened word limit reject the history it labels.
 */
function stripWords(kind: TrackKind): TrackKind {
  if (kind.kind !== 'numeric' || kind.words === undefined) {
    return kind
  }
  const { words: _dropped, ...rest } = kind
  return rest
}

function readMeasurement(
  value: unknown,
  where: string,
  problems: Problems,
): Measurement | null {
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} has an invalid measurement.`, where)
    return null
  }
  const trackId = readString(value, 'trackId', where, problems)
  const raw = value['value']
  if (trackId === null) {
    return null
  }
  if (typeof raw !== 'number' && typeof raw !== 'boolean' && typeof raw !== 'string') {
    problems.add(
      'backup.measurement.value.type',
      `${where} has a measurement whose value is not a number, a yes/no or an option.`,
      where,
    )
    return null
  }
  if (typeof raw === 'number' && !Number.isFinite(raw)) {
    problems.add(
      'backup.measurement.value.type',
      `${where} has a measurement with a value that is not a finite number.`,
      where,
    )
    return null
  }
  const note = readOptionalString(value, 'note', where, problems)
  if (note === null) {
    return null
  }
  return { trackId, value: raw, ...(note === undefined ? {} : { note }) }
}

function readEntry(value: unknown, index: number, problems: Problems): Entry | null {
  const where = `Entry ${String(index + 1)}`
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} is not an object.`, where)
    return null
  }

  const id = readString(value, 'id', where, problems)
  const at = readTimestampField(value, 'at', where, problems)
  const createdAt = readTimestampField(value, 'createdAt', where, problems)
  const updatedAt = readTimestampField(value, 'updatedAt', where, problems)
  const tagIds = readStringArray(value, 'tagIds', where, problems)
  const note = readOptionalString(value, 'note', where, problems)
  const templateId = readOptionalString(value, 'templateId', where, problems)

  const rawMeasurements = value['measurements']
  if (!Array.isArray(rawMeasurements)) {
    problems.add('backup.field.type', `${where} is missing its measurements.`, where)
    return null
  }
  const measurements: Measurement[] = []
  for (const raw of rawMeasurements) {
    const measurement = readMeasurement(raw, where, problems)
    if (measurement === null) {
      return null
    }
    measurements.push(measurement)
  }

  if (
    id === null ||
    at === null ||
    createdAt === null ||
    updatedAt === null ||
    tagIds === null ||
    note === null ||
    templateId === null
  ) {
    return null
  }

  return {
    id,
    at,
    tagIds,
    measurements,
    createdAt,
    updatedAt,
    ...(note === undefined ? {} : { note }),
    ...(templateId === undefined ? {} : { templateId }),
  }
}

function readTag(value: unknown, index: number, problems: Problems): Tag | null {
  const where = `Tag ${String(index + 1)}`
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} is not an object.`, where)
    return null
  }
  const id = readString(value, 'id', where, problems)
  const name = readString(value, 'name', where, problems)
  const colour = readOptionalString(value, 'colour', where, problems)
  if (id === null || name === null || colour === null) {
    return null
  }
  return { id, name, ...(colour === undefined ? {} : { colour }) }
}

function readTemplate(
  value: unknown,
  index: number,
  problems: Problems,
): CheckInTemplate | null {
  const where = `Check-in ${String(index + 1)}`
  if (!isRecord(value)) {
    problems.add('backup.field.type', `${where} is not an object.`, where)
    return null
  }
  const id = readString(value, 'id', where, problems)
  const name = readString(value, 'name', where, problems)
  const trackIds = readStringArray(value, 'trackIds', where, problems)
  const defaultTagIds = readStringArray(value, 'defaultTagIds', where, problems)
  const position = readNumber(value, 'position', where, problems)
  const archived = readBoolean(value, 'archived', where, problems)
  const createdAt = readTimestampField(value, 'createdAt', where, problems)
  const updatedAt = readTimestampField(value, 'updatedAt', where, problems)
  const description = readOptionalString(value, 'description', where, problems)

  if (
    id === null ||
    name === null ||
    trackIds === null ||
    defaultTagIds === null ||
    position === null ||
    archived === null ||
    createdAt === null ||
    updatedAt === null ||
    description === null
  ) {
    return null
  }

  return {
    id,
    name,
    trackIds,
    defaultTagIds,
    position,
    archived,
    createdAt,
    updatedAt,
    ...(description === undefined ? {} : { description }),
  }
}

function readSettings(value: unknown, problems: Problems): Record<string, string> | null {
  if (value === undefined) {
    // Tolerated: a backup written before settings existed is still a backup of
    // everything that did.
    return {}
  }
  if (!isRecord(value)) {
    problems.add(
      'backup.field.type',
      'The settings section is not an object.',
      'settings',
    )
    return null
  }
  const settings: Record<string, string> = {}
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw !== 'string') {
      problems.add('backup.field.type', `The setting "${key}" is not text.`, 'settings')
      return null
    }
    settings[key] = raw
  }
  return settings
}

function readArray(
  container: Record<string, unknown>,
  key: string,
  problems: Problems,
): unknown[] | null {
  const value = container[key]
  if (value === undefined) {
    return []
  }
  if (!Array.isArray(value)) {
    problems.add('backup.field.type', `The ${key} section is not a list.`, key)
    return null
  }
  // `Array.isArray` narrows to `any[]`, which would let anything through
  // untyped from here on. Each element is checked individually anyway.
  return value as unknown[]
}

/** Duplicate ids inside one file: the last would silently win on import. */
function checkUniqueIds(
  items: readonly { readonly id: string }[],
  label: string,
  problems: Problems,
): void {
  const seen = new Set<string>()
  for (const item of items) {
    if (seen.has(item.id)) {
      problems.add(
        'backup.duplicateId',
        `This file contains two ${label} with the id "${item.id}".`,
        label,
      )
    }
    seen.add(item.id)
  }
}

/**
 * Parses, migrates and validates a backup file.
 *
 * Every failure is a value, and every message is one a user can act on: this is
 * the screen where "invalid JSON at position 4821" is the difference between
 * trying a different file and giving up on months of data.
 */
export function readBackup(
  text: string,
  options: { readonly migrations?: readonly BackupMigration[] } = {},
): Result<Backup> {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error) {
    return err<Backup>(
      invalid(
        'backup.notJson',
        `That file is not readable as JSON${error instanceof Error ? ` — ${error.message}` : ''}. Nothing has been changed.`,
      ),
    )
  }

  if (!isRecord(parsed) || parsed['format'] !== BACKUP_FORMAT) {
    return err<Backup>(
      invalid(
        'backup.notABackup',
        'That is not an anytracker backup file. Nothing has been changed.',
      ),
    )
  }

  const schemaVersion = parsed['schemaVersion']
  if (typeof schemaVersion !== 'number' || !Number.isInteger(schemaVersion)) {
    return err<Backup>(
      invalid(
        'backup.version.missing',
        'That backup does not say which version wrote it.',
      ),
    )
  }

  if (schemaVersion > SCHEMA_VERSION) {
    // Refused, never partially read. A stale build that dropped the fields it
    // did not recognise and wrote the result back would destroy data a newer
    // build recorded — and the user would have no way to know (ADR-0003).
    return err<Backup>(
      invalid(
        'backup.schemaTooNew',
        `This backup was saved by a newer version of the app (data version ${String(schemaVersion)}, ` +
          `this build understands ${String(SCHEMA_VERSION)}). Update the app and try again. Nothing has been changed.`,
      ),
    )
  }

  const migrated = applyMigrations(
    parsed,
    schemaVersion,
    SCHEMA_VERSION,
    options.migrations,
  )
  if (!migrated.ok) {
    return err<Backup>(...migrated.errors)
  }
  if (!isRecord(migrated.value)) {
    return err<Backup>(
      invalid('backup.malformed', 'That backup could not be brought up to date.'),
    )
  }

  const problems = new Problems()
  const rawData = migrated.value['data']
  if (!isRecord(rawData)) {
    return err<Backup>(
      invalid('backup.malformed', 'That backup file has no data section.', 'data'),
    )
  }

  const exportedAt = readTimestampField(
    migrated.value,
    'exportedAt',
    'The backup',
    problems,
  )
  const appVersion = readOptionalString(
    migrated.value,
    'appVersion',
    'The backup',
    problems,
  )

  const rawTracks = readArray(rawData, 'tracks', problems)
  const rawEntries = readArray(rawData, 'entries', problems)
  const rawTags = readArray(rawData, 'tags', problems)
  const rawTemplates = readArray(rawData, 'templates', problems)
  const settings = readSettings(rawData['settings'], problems)

  if (
    rawTracks === null ||
    rawEntries === null ||
    rawTags === null ||
    rawTemplates === null ||
    settings === null ||
    exportedAt === null ||
    appVersion === null
  ) {
    return err<Backup>(...problems.collected())
  }

  const tracks = rawTracks.map((raw, index) => readTrack(raw, index, problems))
  const entries = rawEntries.map((raw, index) => readEntry(raw, index, problems))
  const tags = rawTags.map((raw, index) => readTag(raw, index, problems))
  const templates = rawTemplates.map((raw, index) => readTemplate(raw, index, problems))

  if (problems.any) {
    return err<Backup>(...problems.collected())
  }

  const data: BackupData = {
    tracks: tracks.filter((track): track is Track => track !== null),
    entries: entries.filter((entry): entry is Entry => entry !== null),
    tags: tags.filter((tag): tag is Tag => tag !== null),
    templates: templates.filter((t): t is CheckInTemplate => t !== null),
    settings,
  }

  checkUniqueIds(data.tracks, 'tracks', problems)
  checkUniqueIds(data.entries, 'entries', problems)
  checkUniqueIds(data.tags, 'tags', problems)
  checkUniqueIds(data.templates, 'check-ins', problems)

  const actual = countOf(data)
  const claimed = migrated.value['counts']
  if (isRecord(claimed)) {
    for (const [key, count] of Object.entries(actual)) {
      const stated = claimed[key]
      if (typeof stated === 'number' && stated !== count) {
        // A file truncated at exactly the wrong place still parses. This is the
        // only thing that catches it.
        problems.add(
          'backup.counts.mismatch',
          `This file says it holds ${String(stated)} ${key} but contains ${String(count)}. It may be incomplete.`,
          key,
        )
      }
    }
  }

  if (problems.any) {
    return err<Backup>(...problems.collected())
  }

  return ok({
    format: BACKUP_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    appVersion: appVersion ?? 'unknown',
    exportedAt,
    counts: actual,
    data,
  })
}
